/**
 * The moderation queue, served at /admin from the site's own origin (see
 * notes/DESIGN-admin-review-ui.md). Same origin means no CORS change and no new
 * infrastructure; it also means it works from a phone, which is most of the
 * point.
 *
 * The token is the only gate, and it is held in sessionStorage so it dies with
 * the tab — a moderator on a shared or mobile browser should not leave a working
 * admin credential behind. Any 401 from any route clears it and drops back to
 * the gate; the API, not this component, is the actual boundary.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { hideSubmission, listQueue, moderate } from '../lib/adminApi';
import { AdminOverview } from './AdminOverview';
import type { SubmissionItem, SubmissionStatus } from '../shared/types';

const TOKEN_KEY = 'ddd-admin-token';

/** Top-level view: the analytics overview, or the moderation queue. */
type AdminView = 'overview' | 'queue';

const TABS: { status: SubmissionStatus; label: string }[] = [
  { status: 'pending', label: 'Pending' },
  { status: 'approved', label: 'Approved' },
  { status: 'rejected', label: 'Rejected' },
];

// Storage access throws outright in some privacy modes; a moderator who cannot
// persist the token should still get a usable gate rather than a blank page.
function readToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(TOKEN_KEY);
    else sessionStorage.setItem(TOKEN_KEY, value);
  } catch {
    /* in-memory only for this session */
  }
}

function stamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Admin() {
  const [token, setToken] = useState<string | null>(readToken);
  const [draft, setDraft] = useState('');
  const [gateError, setGateError] = useState<string | null>(null);

  // Defaults to the queue so the moderation flow (and its tests) is unchanged;
  // Overview is the first view a moderator can switch to.
  const [view, setView] = useState<AdminView>('queue');
  const [tab, setTab] = useState<SubmissionStatus>('pending');
  const [items, setItems] = useState<SubmissionItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const signOut = useCallback((message: string | null) => {
    writeToken(null);
    setToken(null);
    setItems([]);
    setCursor(undefined);
    setError(null);
    setNotice(null);
    setGateError(message);
  }, []);

  useEffect(() => {
    // No token means no request, ever: the gate must not probe the API. The
    // Overview view fetches its own aggregates, so the queue stays idle there.
    if (!token || view !== 'queue') return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotice(null);

    listQueue(tab, token)
      .then((page) => {
        if (cancelled) return;
        setItems(page.submissions);
        setCursor(page.nextCursor);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut('That password was rejected.');
          return;
        }
        setItems([]);
        setCursor(undefined);
        setError('Could not load the queue.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, tab, view, signOut]);

  function onUnlock(event: React.FormEvent) {
    event.preventDefault();
    const value = draft.trim();
    if (!value) {
      setGateError('Enter the password.');
      return;
    }
    writeToken(value);
    setGateError(null);
    setDraft('');
    setToken(value);
  }

  function selectTab(next: SubmissionStatus) {
    if (next === tab) return;
    // Clear first: a stale page under a new heading reads as the wrong queue.
    setItems([]);
    setCursor(undefined);
    setTab(next);
  }

  async function decide(item: SubmissionItem, status: SubmissionStatus) {
    if (!token) return;

    const index = items.findIndex((i) => i.id === item.id);
    // Optimistic — the row leaves on click and is put back below if the POST
    // fails, so a queue of fifty is fifty clicks and not fifty round trips.
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    setError(null);
    setNotice(null);

    try {
      await moderate(item.id, status, token);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 404) {
        // Already decided — by another tab, or by moderate.sh. The row is
        // correctly gone, so this is a note, not the error banner.
        setNotice('That one had already been moderated.');
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        signOut('That password was rejected.');
        return;
      }
      setItems((prev) => {
        if (prev.some((i) => i.id === item.id)) return prev;
        const next = [...prev];
        next.splice(Math.max(0, Math.min(index, next.length)), 0, item);
        return next;
      });
      setError(
        err instanceof Error ? err.message : 'Could not save that decision.',
      );
    }
  }

  async function makePrivate(item: SubmissionItem) {
    if (!token) return;
    setError(null);
    setNotice(null);

    try {
      await hideSubmission(item.id, token);
      // Keep the row on the Approved tab -- it is still an approved submission,
      // just no longer on the public wall. Flip its stored flag so the "Private"
      // indicator appears and the button disappears (both key off public).
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, public: false } : i)),
      );
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 404) {
        // Already changed -- by another tab, or it is no longer approved. Reflect
        // the intended end state locally so the button clears; it is a note, not
        // the error banner.
        setNotice('That one had already been changed.');
        setItems((prev) =>
          prev.map((i) => (i.id === item.id ? { ...i, public: false } : i)),
        );
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        signOut('That password was rejected.');
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not make that private.');
    }
  }

  async function loadMore() {
    if (!cursor || !token) return;
    setLoading(true);
    try {
      const page = await listQueue(tab, token, cursor);
      setItems((prev) => [...prev, ...page.submissions]);
      setCursor(page.nextCursor);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 401) {
        signOut('That password was rejected.');
        return;
      }
      setError('Could not load more.');
    } finally {
      setLoading(false);
    }
  }

  const heading = (
    <h1 className="admin__title">
      Delete DoorDash <span className="admin__slash">/</span> Review
    </h1>
  );

  if (!token) {
    return (
      <main className="admin">
        <header className="admin__bar">{heading}</header>

        <form className="admin__gate" onSubmit={onUnlock}>
          <div className="field">
            <label htmlFor="admin-token">Password</label>
            <input
              id="admin-token"
              type="password"
              value={draft}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>

          {gateError && (
            <p className="form__error" role="alert">
              {gateError}
            </p>
          )}

          <button type="submit" className="btn btn--primary">
            Enter
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="admin">
      <header className="admin__bar">
        {heading}
        <button
          type="button"
          className="admin__signout"
          onClick={() => signOut(null)}
        >
          Sign out
        </button>
      </header>

      <nav className="admin__views" aria-label="Admin sections">
        <button
          type="button"
          className={`admin__view${view === 'overview' ? ' admin__view--on' : ''}`}
          aria-current={view === 'overview'}
          onClick={() => setView('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          className={`admin__view${view === 'queue' ? ' admin__view--on' : ''}`}
          aria-current={view === 'queue'}
          onClick={() => setView('queue')}
        >
          Queue
        </button>
      </nav>

      {view === 'overview' && (
        <AdminOverview token={token} onAuthError={(message) => signOut(message)} />
      )}

      {view === 'queue' && (
      <>
      <div className="admin__tabs" role="tablist" aria-label="Moderation queue">
        {TABS.map((t) => (
          <button
            key={t.status}
            type="button"
            role="tab"
            id={`admin-tab-${t.status}`}
            aria-selected={tab === t.status}
            aria-controls="admin-queue"
            className={`admin__tab${tab === t.status ? ' admin__tab--on' : ''}`}
            onClick={() => selectTab(t.status)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        className="admin__queue"
        id="admin-queue"
        role="tabpanel"
        aria-labelledby={`admin-tab-${tab}`}
      >
        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}

        {notice && (
          <p className="notice" role="status">
            {notice}
          </p>
        )}

        {items.length > 0 && (
          <ul className="admin__list">
            {items.map((item) => (
              <li key={item.id} className="admin-row">
                {item.photoKey ? (
                  // Relative on purpose: the photos and the site are the same
                  // CloudFront distribution, so this stays correct for free once
                  // the custom domain lands. Dev has no S3 behind it, so photos
                  // only render against test/prod.
                  <a
                    className="admin-row__photo-link"
                    href={`/${item.photoKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="admin-row__photo"
                      src={`/${item.photoKey}`}
                      alt={`Photo submitted by ${item.name}`}
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <p className="admin-row__nophoto">No photo</p>
                )}

                <div className="admin-row__body">
                  <p className="admin-row__name">{item.name}</p>
                  {item.note && <p className="admin-row__note">{item.note}</p>}
                  {/* Country is internal-only tracking; show it only when it is
                      not the US (absent or 'US' is the common case and stays
                      implicit, matching how state/ward already read). */}
                  {item.country && item.country !== 'US' && (
                    <p className="admin-row__country">Country: {item.country}</p>
                  )}
                  {/* Older records predate the state field, so guard for it. */}
                  {item.state && <p className="admin-row__state">State: {item.state}</p>}
                  {item.ward && <p className="admin-row__ward">Ward {item.ward}</p>}
                  {/* Consent flag: absent/true is the common case and stays
                      implicit. Call out only a private submission, so a moderator
                      knows approving it will NOT put it on the public wall. */}
                  {item.public === false && (
                    <p className="admin-row__private">Private (kept off the wall)</p>
                  )}
                  <time className="admin-row__date" dateTime={item.createdAt}>
                    {stamp(item.createdAt)}
                  </time>

                  {item.email && (
                    // Collapsed by default: the address is moderator-visible but
                    // it is a contact detail, and an open queue gets screenshotted.
                    <details className="admin-row__contact">
                      <summary>Contact address</summary>
                      <p>{item.email}</p>
                    </details>
                  )}
                </div>

                {item.status === 'pending' && (
                  <div className="admin-row__actions">
                    <button
                      type="button"
                      className="btn btn--primary admin-row__action"
                      aria-label={`Approve ${item.name}`}
                      onClick={() => decide(item, 'approved')}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost admin-row__action"
                      aria-label={`Reject ${item.name}`}
                      onClick={() => decide(item, 'rejected')}
                    >
                      Reject
                    </button>
                  </div>
                )}

                {/* Approved tab only: let a moderator pull an already-public post
                    off the wall. ONE-WAY -- an already-private row (public ===
                    false) shows no button, just the "Private" indicator above. */}
                {tab === 'approved' && item.public !== false && (
                  <div className="admin-row__actions">
                    <button
                      type="button"
                      className="btn btn--ghost admin-row__action"
                      aria-label={`Make ${item.name} private`}
                      onClick={() => makePrivate(item)}
                    >
                      Make private
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {loading && <p className="notice">Loading…</p>}

        {!loading && !error && items.length === 0 && (
          <p className="notice">Nothing {tab}.</p>
        )}

        {cursor && !loading && (
          <button type="button" className="btn btn--ghost" onClick={loadMore}>
            Load more
          </button>
        )}
      </div>
      </>
      )}
    </main>
  );
}
